import { createAsyncThunk } from '@reduxjs/toolkit';
import type { AppDispatch, RootState } from '@/shared/store/store';
import { setLoading, setError } from '@/shared/store/slices/generalSlice';

interface AsyncThunkConfig {
    state: RootState;
    dispatch: AppDispatch;
    rejectValue: string;
}

/**
 * Універсальний async thunk з автоматичним керуванням loading/error
 * і підтримкою короткого ключа ("header" замість "header/fetchHeader")
 */
export function createAppAsyncThunk<Returned, ThunkArg = void>(
    typePrefix: string,
    payloadCreator: (
        arg: ThunkArg,
        thunkAPI: {
            dispatch: AppDispatch;
            state: RootState;
        },
    ) => Promise<Returned>,
) {
    return createAsyncThunk<Returned, ThunkArg, AsyncThunkConfig>(typePrefix, async (arg, thunkAPI) => {
        const { dispatch } = thunkAPI;

        // отримуємо короткий ключ, наприклад "header"
        const shortKey = typePrefix.split('/')[0];

        // включаємо loading для обох ключів
        dispatch(setLoading({ key: typePrefix, value: true }));
        dispatch(setLoading({ key: shortKey, value: true }));

        // очищаємо помилки
        dispatch(setError({ key: typePrefix, value: null }));
        dispatch(setError({ key: shortKey, value: null }));

        try {
            const result = await payloadCreator(arg, {
                dispatch: thunkAPI.dispatch,
                state: thunkAPI.getState(),
            });
            return result;
        } catch (error: any) {
            const errorMsg = error?.message || 'Unknown error';

            // зберігаємо помилки для обох ключів
            dispatch(setError({ key: typePrefix, value: errorMsg }));
            dispatch(setError({ key: shortKey, value: errorMsg }));

            return thunkAPI.rejectWithValue(errorMsg);
        } finally {
            // вимикаємо loading для обох ключів
            dispatch(setLoading({ key: typePrefix, value: false }));
            dispatch(setLoading({ key: shortKey, value: false }));
        }
    });
}
